import type {
  ReferralActivityApiItem,
  ReferralActivityItem,
  ReferralActivityType,
  ReferralEarningsApiEntry,
  ReferralEarningsEntry,
  ReferralEarningsView,
  ReferralHistoryApiData,
  ReferralHistoryItem,
  ReferralHistoryStats,
  ReferralHistoryView,
  ReferralLedgerType,
  ReferralListItem,
  ReferralMeCode,
  ReferralStatus,
  ReferralSummary,
} from '@/types/referral';

export const buildReferralLink = (code: string) =>
  `https://sellyt.shop/sign-up?ref=${encodeURIComponent(code)}`;

const ACTIVE_REFERRAL_LIMIT = 50;
const REFERRAL_WITHDRAW_MINIMUM = 5000;

const LEDGER_TYPES: ReferralLedgerType[] = [
  'ACTIVATION',
  'ONGOING',
  'UPGRADE_BONUS',
  'SUBSCRIPTION_CREDIT',
  'WITHDRAWAL',
];

function asLedgerType(value: string | undefined): ReferralLedgerType {
  if (value && LEDGER_TYPES.includes(value as ReferralLedgerType)) {
    return value as ReferralLedgerType;
  }
  return 'ONGOING';
}

export function parseReferralCode(payload: unknown): string {
  if (typeof payload === 'string' && payload.trim()) {
    return payload.trim();
  }
  if (!payload || typeof payload !== 'object') return '';

  const record = payload as Record<string, unknown>;
  const candidates = [record.code, record.referralCode, record.referral_code];

  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
}

export function parseReferralMeCode(payload: unknown): ReferralMeCode {
  const code = parseReferralCode(payload);

  if (!payload || typeof payload !== 'object') {
    return {
      code,
      link: code ? buildReferralLink(code) : '',
    };
  }

  const record = payload as Record<string, unknown>;
  const link =
    typeof record.link === 'string' && record.link.trim()
      ? record.link.trim()
      : code
        ? buildReferralLink(code)
        : '';

  return { code, link };
}

function extractEarningsRows(payload: unknown): ReferralEarningsApiEntry[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as ReferralEarningsApiEntry[];

  if (typeof payload !== 'object') return [];
  const record = payload as Record<string, unknown>;

  if (Array.isArray(record.data)) {
    return record.data as ReferralEarningsApiEntry[];
  }
  if (Array.isArray(record.entries)) {
    return record.entries as ReferralEarningsApiEntry[];
  }
  if (
    record.data &&
    typeof record.data === 'object' &&
    Array.isArray((record.data as { data?: unknown }).data)
  ) {
    return (record.data as { data: ReferralEarningsApiEntry[] }).data;
  }

  return [];
}

function mapEarningsEntry(
  item: ReferralEarningsApiEntry,
  index: number
): ReferralEarningsEntry {
  const createdAt =
    item.createdAt ?? item.created_at ?? new Date().toISOString();
  const amount = typeof item.amount === 'number' ? item.amount : 0;
  const balanceAfter =
    typeof item.balanceAfter === 'number'
      ? item.balanceAfter
      : typeof item.balance_after === 'number'
        ? item.balance_after
        : 0;

  return {
    id: String(item.id ?? `earning-${index}`),
    createdAt,
    description:
      item.description ??
      item.message ??
      (amount >= 0 ? 'Referral earning' : 'Referral debit'),
    amount,
    balanceAfter,
    type: asLedgerType(item.type),
  };
}

function sumThisMonthEarnings(entries: ReferralEarningsEntry[]): number {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();

  return entries.reduce((sum, entry) => {
    if (entry.amount <= 0) return sum;
    const created = new Date(entry.createdAt);
    if (
      Number.isNaN(created.getTime()) ||
      created.getMonth() !== month ||
      created.getFullYear() !== year
    ) {
      return sum;
    }
    return sum + entry.amount;
  }, 0);
}

export function mapReferralEarningsResponse(
  payload: unknown
): ReferralEarningsView {
  const rows = extractEarningsRows(payload).map(mapEarningsEntry);
  const sorted = [...rows].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const record =
    payload && typeof payload === 'object'
      ? (payload as Record<string, unknown>)
      : {};

  const availableBalance =
    typeof record.availableBalance === 'number'
      ? record.availableBalance
      : typeof record.available_balance === 'number'
        ? record.available_balance
        : (sorted[0]?.balanceAfter ?? 0);

  const totalEarnings =
    typeof record.totalEarnings === 'number'
      ? record.totalEarnings
      : typeof record.total_earnings === 'number'
        ? record.total_earnings
        : sorted.reduce(
            (sum, entry) => (entry.amount > 0 ? sum + entry.amount : sum),
            0
          );

  const thisMonthEarnings =
    typeof record.thisMonthEarnings === 'number'
      ? record.thisMonthEarnings
      : typeof record.this_month_earnings === 'number'
        ? record.this_month_earnings
        : sumThisMonthEarnings(sorted);

  return {
    entries: sorted,
    availableBalance,
    totalEarnings,
    thisMonthEarnings,
  };
}

export function buildReferralSummary(params: {
  code: string;
  link?: string;
  earnings: ReferralEarningsView;
  history: ReferralHistoryView;
  unreadActivityCount?: number;
}): ReferralSummary {
  const { code, link, earnings, history, unreadActivityCount = 0 } = params;
  const availableBalance = earnings.availableBalance;

  return {
    referralCode: code,
    referralLink: link?.trim() || buildReferralLink(code),
    totalEarnings: earnings.totalEarnings || history.stats.totalEarnings,
    availableBalance,
    thisMonthEarnings: earnings.thisMonthEarnings,
    activeReferralsCount: history.active.length,
    unreadActivityCount,
    canWithdraw: availableBalance >= REFERRAL_WITHDRAW_MINIMUM,
    activeReferralLimit: ACTIVE_REFERRAL_LIMIT,
  };
}

const ACTIVE_STATUSES: ReferralStatus[] = [
  'SIGNED_UP',
  'CHANNEL_CONNECTED',
  'QUALIFIED',
  'ACTIVE',
];

function asReferralStatus(status: string): ReferralStatus {
  if (
    status === 'SIGNED_UP' ||
    status === 'CHANNEL_CONNECTED' ||
    status === 'QUALIFIED' ||
    status === 'ACTIVE' ||
    status === 'COMPLETED' ||
    status === 'EXPIRED'
  ) {
    return status;
  }
  return 'SIGNED_UP';
}

export function mapReferralActivityItem(
  item: ReferralActivityApiItem
): ReferralActivityItem {
  return {
    id: String(item.id),
    referralId: String(item.referralId),
    referralName: '',
    eventType: item.eventType as ReferralActivityType,
    message: item.message,
    amount: item.amount ?? undefined,
    createdAt: item.createdAt,
    isRead: item.isRead,
  };
}

function mapHistoryReferralItem(
  item: ReferralHistoryApiData['referrals'][number]
): ReferralHistoryItem {
  const status = asReferralStatus(item.status);
  let monthProgress: string | undefined;

  if (item.qualificationDate) {
    monthProgress = `Qualified ${new Date(
      item.qualificationDate
    ).toLocaleDateString('en-NG', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  } else if (item.earningsWindowEnd) {
    monthProgress = `Earnings window ends ${new Date(
      item.earningsWindowEnd
    ).toLocaleDateString('en-NG', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }

  return {
    id: String(item.id),
    name: item.referredName,
    status,
    totalEarnings: item.totalEarnings,
    monthProgress,
  };
}

export function mapReferralListItem(
  item: ReferralHistoryApiData['referrals'][number]
): ReferralListItem {
  const status = asReferralStatus(item.status);

  return {
    id: String(item.id),
    name: item.referredName,
    status,
    signupDate: item.createdAt,
    monthIndicator: item.qualificationDate ? 'Qualified' : undefined,
    thisMonthEarnings: 0,
    totalEarnings: item.totalEarnings,
    upgradeBonusClaimed: false,
  };
}

export function mapReferralHistoryResponse(
  data: ReferralHistoryApiData | null | undefined,
  activeReferralLimit = 50
): ReferralHistoryView {
  const referrals = data?.referrals ?? [];
  const active: ReferralHistoryItem[] = [];
  const completed: ReferralHistoryItem[] = [];
  const expired: ReferralHistoryItem[] = [];

  for (const referral of referrals) {
    const item = mapHistoryReferralItem(referral);
    if (referral.status === 'COMPLETED') {
      completed.push(item);
    } else if (referral.status === 'EXPIRED') {
      expired.push(item);
    } else if (ACTIVE_STATUSES.includes(item.status)) {
      active.push(item);
    } else {
      active.push(item);
    }
  }

  const best =
    data?.stats.bestReferralId != null
      ? referrals.find((r) => r.id === data.stats.bestReferralId)
      : undefined;

  const stats: ReferralHistoryStats = {
    totalReferrals: data?.stats.totalReferrals ?? 0,
    totalQualified: data?.stats.totalQualified ?? 0,
    totalEarnings: data?.stats.totalEarned ?? 0,
    bestReferralName: best?.referredName ?? '—',
    bestReferralEarnings: best?.totalEarnings ?? 0,
    activeSlotsRemaining: Math.max(
      0,
      activeReferralLimit - (data?.stats.totalReferrals ?? 0)
    ),
  };

  return {
    stats,
    referrals: referrals.map(mapReferralListItem),
    active,
    completed,
    expired,
  };
}

export function countUnreadActivity(activity: ReferralActivityItem[]): number {
  return activity.filter((item) => !item.isRead).length;
}
